<template>
  <div class="restrict-rules">
    <div class="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-6">
      <div>
        <h4 class="fw-bolder mb-1">Regras de restrição</h4>
        <div class="text-muted fs-7">Sem regra para um horário, vale o padrão do profissional.</div>
      </div>
      <button v-if="canManage && !editing" type="button" class="btn btn-primary btn-sm" @click="startCreate">
        <i class="bi bi-plus-lg"></i> Adicionar regra
      </button>
    </div>

    <div v-if="loading" class="text-center text-muted py-10">
      <span class="spinner-border spinner-border-sm align-middle me-2"></span>Carregando regras…
    </div>

    <template v-else-if="!editing">
      <div v-if="rules.length === 0" class="text-center border border-dashed rounded py-12 px-6">
        <i class="bi bi-shield-check fs-2x text-muted"></i>
        <div class="fw-bold fs-5 mt-3">Nenhuma regra cadastrada</div>
        <div class="text-muted mb-5">Crie uma regra para limitar horários por dia, faixa, especialidade, forma de recebimento ou convênio.</div>
        <button v-if="canManage" type="button" class="btn btn-light-primary btn-sm" @click="startCreate">
          <i class="bi bi-plus-lg"></i> Adicionar regra
        </button>
      </div>

      <div v-else class="table-responsive">
        <table class="table table-row-bordered table-row-gray-200 align-middle gs-0 gy-4">
          <thead>
          <tr class="fw-bold fs-7 text-muted text-uppercase">
            <th>Dias</th>
            <th>Faixa / período</th>
            <th>Especialidades</th>
            <th>Formas / convênios</th>
            <th class="text-center">Status</th>
            <th v-if="canManage" class="text-end">Ações</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="rule in rules" :key="rule.id_sched_restrict_rule" :class="{'opacity-50': !rule.active}">
            <td>
              <span v-if="rule.all_week_days" class="badge badge-light-primary">Todos os dias</span>
              <template v-else>
                <span v-for="day in rule.week_days" :key="day" class="badge badge-light-primary me-1">{{ dayShort(day) }}</span>
              </template>
            </td>
            <td>
              <div class="fw-bold">
                <i class="bi bi-clock text-muted me-1"></i>
                {{ rule.time_begin ? `${rule.time_begin} – ${rule.time_end}` : 'Dia inteiro' }}
              </div>
              <div v-if="rule.date_begin || rule.date_end" class="text-muted fs-7">
                <i class="bi bi-calendar3 me-1"></i>{{ periodLabel(rule) }}
              </div>
            </td>
            <td>
              <span v-if="rule.all_specs" class="badge badge-light">Todas</span>
              <template v-else>
                <span v-for="name in specNames(rule)" :key="name" class="badge badge-light-info me-1 mb-1">{{ name }}</span>
              </template>
            </td>
            <td>
              <span v-if="rule.all_paymeths" class="badge badge-light">Todas as formas</span>
              <template v-else>
                <span v-for="name in paymethNames(rule)" :key="name" class="badge badge-light-warning me-1 mb-1">{{ name }}</span>
              </template>
              <div v-if="rule.insurance_max_perc !== null && rule.insurance_max_perc !== undefined" class="text-muted fs-7 mt-1">
                Teto de convênios: <span class="fw-bold text-gray-800">{{ rule.insurance_max_perc }}%</span>
              </div>
            </td>
            <td class="text-center">
              <span class="badge" :class="rule.active ? 'badge-light-success' : 'badge-light-secondary text-muted'">
                {{ rule.active ? 'Ativa' : 'Inativa' }}
              </span>
              <div v-if="rule.patient_only" class="mt-1">
                <span class="badge badge-light-primary"><i class="bi bi-person-check-fill text-primary me-1"></i>Só paciente</span>
              </div>
            </td>
            <td v-if="canManage" class="text-end text-nowrap">
              <button type="button" class="btn btn-icon btn-sm btn-light-primary me-2" title="Editar" @click="startEdit(rule)">
                <i class="bi bi-pencil"></i>
              </button>
              <button type="button" class="btn btn-icon btn-sm btn-light-danger" title="Excluir" @click="confirmRemove(rule)">
                <i class="bi bi-trash"></i>
              </button>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </template>

    <form v-if="editing" class="card card-bordered" @submit.prevent="save">
      <div class="card-header min-h-60px">
        <h5 class="card-title fw-bolder mb-0">{{ editingId ? 'Editar regra' : 'Nova regra' }}</h5>
        <div class="card-toolbar">
          <div class="form-check form-switch form-check-custom form-check-solid">
            <input id="restrict-rule-active" class="form-check-input" type="checkbox" v-model="form.active">
            <label class="form-check-label fw-bold" for="restrict-rule-active">{{ form.active ? 'Ativa' : 'Inativa' }}</label>
          </div>
        </div>
      </div>

      <div class="card-body">
        <label for="restrict-rule-patient-only" class="patient-only d-flex align-items-center gap-4 rounded px-5 py-4 mb-6"
               :class="{'is-active': form.patient_only}">
          <span class="patient-only__icon d-flex align-items-center justify-content-center rounded-circle">
            <i class="bi" :class="form.patient_only ? 'bi-person-check-fill' : 'bi-people-fill'"></i>
          </span>
          <span class="flex-grow-1">
            <span class="d-block fw-bolder fs-6 text-gray-800">
              Vale apenas para o paciente
              <span v-if="form.patient_only" class="badge badge-light-primary ms-2">Ligado</span>
            </span>
            <span class="d-block text-muted fs-7 mt-1">
              {{ form.patient_only
                ? 'Só o agendamento feito pelo paciente segue esta regra. A recepção agenda normalmente.'
                : 'A regra vale para o paciente e para a recepção.' }}
            </span>
          </span>
          <span class="form-check form-switch form-check-custom form-check-solid mb-0">
            <input id="restrict-rule-patient-only" class="form-check-input h-25px w-45px" type="checkbox" v-model="form.patient_only">
          </span>
        </label>

        <div class="fw-bolder text-gray-800 mb-4"><i class="bi bi-calendar-week me-2"></i>Quando</div>
        <div class="row mb-6">
          <div class="col-lg-6 mb-4">
            <label class="form-label fw-bold">Dias da semana</label>
            <div class="d-flex flex-wrap gap-2">
              <button type="button" class="btn btn-sm chip" :class="form.all_week_days ? 'btn-primary' : 'btn-light'" @click="form.all_week_days = true">
                Todos
              </button>
              <button v-for="day in weekDays" :key="day.number" type="button" class="btn btn-sm chip"
                      :class="!form.all_week_days && form.week_days.includes(day.number) ? 'btn-primary' : 'btn-light'"
                      @click="toggleDay(day.number)">
                {{ day.short }}
              </button>
            </div>
          </div>
          <div class="col-lg-3 col-md-6 mb-4">
            <label class="form-label fw-bold">Faixa de horário</label>
            <div class="input-group input-group-sm">
              <input class="form-control form-control-solid" v-mask="'##:##'" placeholder="08:00" v-model="form.time_begin">
              <span class="input-group-text">às</span>
              <input class="form-control form-control-solid" v-mask="'##:##'" placeholder="12:00" v-model="form.time_end">
            </div>
            <div class="form-text">Vazio = dia inteiro</div>
          </div>
          <div class="col-lg-3 col-md-6 mb-4">
            <label class="form-label fw-bold">Período</label>
            <div class="input-group input-group-sm">
              <input type="date" class="form-control form-control-solid" v-model="form.date_begin">
              <span class="input-group-text">até</span>
              <input type="date" class="form-control form-control-solid" v-model="form.date_end">
            </div>
            <div class="form-text">Vazio = sem limite de datas</div>
          </div>
        </div>

        <div class="separator separator-dashed mb-6"></div>

        <div class="fw-bolder text-gray-800 mb-4"><i class="bi bi-person-badge me-2"></i>Quem</div>
        <div class="row mb-6">
          <div class="col-12 mb-4">
            <label class="form-label fw-bold">Especialidades</label>
            <div class="btn-group btn-group-sm mb-3 d-flex" style="max-width: 280px">
              <button type="button" class="btn" :class="form.all_specs ? 'btn-primary' : 'btn-light'" @click="form.all_specs = true">Todas</button>
              <button type="button" class="btn" :class="!form.all_specs ? 'btn-primary' : 'btn-light'" @click="form.all_specs = false">Selecionar</button>
            </div>
            <div v-if="!form.all_specs" class="pick-list">
              <label v-for="specialty in specialties" :key="specialty.id_spec" class="form-check form-check-custom form-check-solid form-check-sm">
                <input class="form-check-input" type="checkbox" :value="specialty.id_spec" v-model="form.id_specs">
                <span class="form-check-label">{{ specialty.spec_name }}</span>
              </label>
              <div v-if="specialties.length === 0" class="text-muted fs-7">Nenhuma especialidade vinculada.</div>
            </div>
          </div>
        </div>

        <div class="separator separator-dashed mb-6"></div>

        <div class="fw-bolder text-gray-800 mb-4"><i class="bi bi-wallet2 me-2"></i>Pagamento</div>
        <div class="row">
          <div class="col-lg-6 mb-4">
            <label class="form-label fw-bold">Formas de recebimento</label>
            <div class="btn-group btn-group-sm mb-3 d-flex" style="max-width: 280px">
              <button type="button" class="btn" :class="form.all_paymeths ? 'btn-primary' : 'btn-light'" @click="form.all_paymeths = true">Todas</button>
              <button type="button" class="btn" :class="!form.all_paymeths ? 'btn-primary' : 'btn-light'" @click="form.all_paymeths = false">Selecionar</button>
            </div>
            <div v-if="!form.all_paymeths" class="pick-list">
              <label v-for="paymeth in paymentMethods" :key="paymeth.id_paymeth" class="form-check form-check-custom form-check-solid form-check-sm">
                <input class="form-check-input" type="checkbox" :value="paymeth.id_paymeth" v-model="form.id_paymeths">
                <span class="form-check-label">{{ paymeth.paymeth_name }}</span>
              </label>
              <div v-if="paymentMethods.length === 0" class="text-muted fs-7">Nenhuma forma de recebimento habilitada.</div>
            </div>
          </div>

          <div v-if="convenioAllowed" class="col-lg-6 mb-4">
            <label class="form-label fw-bold">Convênios</label>
            <div class="btn-group btn-group-sm mb-3 d-flex" style="max-width: 280px">
              <button type="button" class="btn" :class="form.all_insurances ? 'btn-primary' : 'btn-light'" @click="form.all_insurances = true">Todos</button>
              <button type="button" class="btn" :class="!form.all_insurances ? 'btn-primary' : 'btn-light'" @click="form.all_insurances = false">Selecionar</button>
            </div>
            <div class="mb-3" style="max-width: 280px">
              <label class="form-label fs-7 text-muted mb-1">Teto geral de convênios</label>
              <div class="input-group input-group-sm">
                <input type="number" min="0" max="100" class="form-control form-control-solid" placeholder="Sem teto" v-model="form.insurance_max_perc">
                <span class="input-group-text">%</span>
              </div>
            </div>
            <div v-if="!form.all_insurances" class="pick-list">
              <div v-for="insurance in insurances" :key="insurance.id_insurance" class="d-flex align-items-center gap-3">
                <label class="form-check form-check-custom form-check-solid form-check-sm flex-grow-1 mb-0">
                  <input class="form-check-input" type="checkbox" :value="insurance.id_insurance" v-model="selectedInsuranceIds">
                  <span class="form-check-label">{{ insurance.insurance_name }}</span>
                </label>
                <div class="input-group input-group-sm w-100px">
                  <input type="number" min="0" max="100" class="form-control form-control-solid" placeholder="teto"
                         :disabled="!selectedInsuranceIds.includes(insurance.id_insurance)"
                         v-model="insurancePerc[insurance.id_insurance]">
                  <span class="input-group-text">%</span>
                </div>
              </div>
              <div v-if="insurances.length === 0" class="text-muted fs-7">Nenhum convênio vinculado.</div>
            </div>
          </div>
        </div>
      </div>

      <div class="card-footer d-flex justify-content-end gap-2">
        <button type="button" class="btn btn-light" :disabled="saving" @click="editing = false">Cancelar</button>
        <button type="submit" class="btn btn-primary" :disabled="saving">
          <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
          Salvar regra
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import {mask} from 'vue-the-mask';
import http from '../../../../helpers/http';
import Alert from '@/helpers/alert';
import Permission from '../../../../helpers/permission';

const emptyForm = () => ({
  all_week_days: true,
  week_days: [],
  time_begin: '',
  time_end: '',
  date_begin: '',
  date_end: '',
  all_specs: true,
  id_specs: [],
  all_paymeths: true,
  id_paymeths: [],
  all_insurances: true,
  insurance_max_perc: '',
  patient_only: false,
  active: true,
});

export default {
  name: 'AvailRestrictRules',
  directives: {mask},
  props: {
    placeId: {required: true},
    professionalId: {required: true},
    specialties: {type: Array, default: () => []},
    preferredSpecId: {default: null},
  },
  data() {
    return {
      loading: false,
      saving: false,
      editing: false,
      editingId: null,
      rules: [],
      paymentMethods: [],
      insurances: [],
      selectedInsuranceIds: [],
      insurancePerc: {},
      form: emptyForm(),
      weekDays: [
        {number: 1, name: 'Domingo', short: 'Dom'},
        {number: 2, name: 'Segunda', short: 'Seg'},
        {number: 3, name: 'Terça', short: 'Ter'},
        {number: 4, name: 'Quarta', short: 'Qua'},
        {number: 5, name: 'Quinta', short: 'Qui'},
        {number: 6, name: 'Sexta', short: 'Sex'},
        {number: 7, name: 'Sábado', short: 'Sáb'},
      ],
    };
  },
  computed: {
    canManage() {
      return Permission.has('Manage Schedule Restrict Rule');
    },
    convenioAllowed() {
      const methods = this.form.all_paymeths
        ? this.paymentMethods
        : this.paymentMethods.filter(method => this.form.id_paymeths.includes(method.id_paymeth));
      return methods.some(method => method.paymeth_type === 'CO');
    },
    baseUrl() {
      return `/places/${this.placeId}/professionals/${this.professionalId}/schedule-restrict-rules`;
    },
  },
  watch: {
    professionalId() {
      this.editing = false;
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      if (!this.placeId || !this.professionalId) {
        return;
      }
      this.loading = true;
      Promise.all([
        http.get(this.baseUrl),
        http.get(`/places/${this.placeId}/professionals/${this.professionalId}/payment-methods`),
        http.get(`/places/${this.placeId}/professionals/${this.professionalId}/insurances`),
      ]).then(([rules, paymeths, insurances]) => {
        this.rules = rules.data;
        this.paymentMethods = (paymeths.data || []).filter(method => Number(method.enabled_to_prof_place) === 1);
        this.insurances = insurances.data;
      }).catch(() => {
        this.rules = [];
      }).finally(() => {
        this.loading = false;
      });
    },
    startCreate() {
      this.editingId = null;
      this.form = emptyForm();
      if (this.preferredSpecId) {
        this.form.all_specs = false;
        this.form.id_specs = [this.preferredSpecId];
      }
      this.selectedInsuranceIds = [];
      this.insurancePerc = {};
      this.editing = true;
    },
    startEdit(rule) {
      http.get(`${this.baseUrl}/${rule.id_sched_restrict_rule}`).then((response) => {
        const data = response.data;
        this.editingId = data.id_sched_restrict_rule;
        this.form = {
          ...emptyForm(),
          ...data,
          time_begin: data.time_begin || '',
          time_end: data.time_end || '',
          date_begin: data.date_begin || '',
          date_end: data.date_end || '',
          insurance_max_perc: data.insurance_max_perc ?? '',
        };
        this.selectedInsuranceIds = (data.insurances || []).map(item => item.id_insurance);
        this.insurancePerc = {};
        (data.insurances || []).forEach(item => {
          this.insurancePerc[item.id_insurance] = item.max_perc ?? '';
        });
        this.editing = true;
      });
    },
    payload() {
      return {
        ...this.form,
        time_begin: this.form.time_begin || null,
        time_end: this.form.time_end || null,
        date_begin: this.form.date_begin || null,
        date_end: this.form.date_end || null,
        insurance_max_perc: this.form.insurance_max_perc === '' ? null : this.form.insurance_max_perc,
        insurances: this.form.all_insurances ? [] : this.selectedInsuranceIds.map(id => ({
          id_insurance: id,
          max_perc: this.insurancePerc[id] === '' || this.insurancePerc[id] === undefined ? null : this.insurancePerc[id],
        })),
      };
    },
    save() {
      this.saving = true;
      const request = this.editingId
        ? http.put(`${this.baseUrl}/${this.editingId}`, this.payload())
        : http.post(this.baseUrl, this.payload());
      request.then(() => {
        Alert.toastSuccess('Regra salva.');
        this.editing = false;
        this.load();
      }).catch((error) => {
        Alert.toastError(error.response?.data?.message || error.response?.data || 'Não foi possível salvar a regra.');
      }).finally(() => {
        this.saving = false;
      });
    },
    confirmRemove(rule) {
      Swal.fire({
        title: 'Excluir regra?',
        text: 'Os horários voltam a seguir o padrão do profissional.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Excluir',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#f1416c',
      }).then((result) => {
        if (result.isConfirmed) {
          this.remove(rule);
        }
      });
    },
    remove(rule) {
      http.delete(`${this.baseUrl}/${rule.id_sched_restrict_rule}`).then(() => {
        Alert.toastSuccess('Regra excluída.');
        this.load();
      }).catch((error) => {
        Alert.toastError(error.response?.data?.message || 'Não foi possível excluir a regra.');
      });
    },
    toggleDay(number) {
      this.form.all_week_days = false;
      const index = this.form.week_days.indexOf(number);
      if (index >= 0) {
        this.form.week_days.splice(index, 1);
      } else {
        this.form.week_days.push(number);
      }
      if (this.form.week_days.length === 0) {
        this.form.all_week_days = true;
      }
    },
    dayShort(number) {
      return this.weekDays.find(item => item.number === number)?.short || number;
    },
    periodLabel(rule) {
      const format = (date) => (date ? date.split('-').reverse().join('/') : '…');
      return `${format(rule.date_begin)} – ${format(rule.date_end)}`;
    },
    specNames(rule) {
      return rule.id_specs.map(id => this.specialties.find(item => item.id_spec === id)?.spec_name || id);
    },
    paymethNames(rule) {
      return rule.id_paymeths.map(id => this.paymentMethods.find(item => item.id_paymeth === id)?.paymeth_name || id);
    },
  },
};
</script>

<style scoped>
.chip {
  min-width: 52px;
}

.patient-only {
  cursor: pointer;
  border: 1px dashed #dbdfe9;
  background-color: #f9f9f9;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.patient-only:hover {
  border-color: #b5b5c3;
}

.patient-only.is-active {
  border-style: solid;
  border-color: var(--bs-primary);
  background-color: var(--bs-primary-light, #f1faff);
}

.patient-only__icon {
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  font-size: 1.25rem;
  color: #a1a5b7;
  background-color: #eff2f5;
}

.patient-only.is-active .patient-only__icon {
  color: #fff;
  background-color: var(--bs-primary);
}

.pick-list {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  max-height: 220px;
  overflow-y: auto;
  padding: 0.75rem 1rem;
  border: 1px dashed #dbdfe9;
  border-radius: 0.475rem;
  background-color: #f9f9f9;
}
</style>
